/* ==========================================================================
   LORENZA — theme.js
   All core interactivity. No dependencies, runs on every device.
   GSAP (animations.js) is desktop-only and purely decorative on top of this.

    1. Utilities (money, scroll lock, fetch helpers)
    2. Scroll reveal
    3. Header + mobile nav
    4. Announcement rotator
    5. Accordions
    6. Cart drawer (render, qty, free-shipping meter, in-drawer upsell)
    7. Add to cart (AJAX)
    8. Post-add-to-cart upsell popup
    9. Product card quick-add
   10. PDP gallery
   11. PDP variants (swatches + sizes + stock + gift wrap)
   12. Complete-the-look (FBT)
   13. Sticky mobile ATC
   14. Size guide modal
   15. Lightbox
   16. Predictive search panel
   17. Collection filter drawer
   18. Reviews pagination
   19. Newsletter
   ========================================================================== */
(function () {
  'use strict';

  var H = window.Lorenza || {};
  var ROUTES = H.routes || {};
  var STR = H.strings || {};
  var ROOT = ROUTES.root || '/';
  if (ROOT.charAt(ROOT.length - 1) !== '/') ROOT += '/';   // '/en' -> '/en/'
  var SHIP_THRESHOLD = (H.shipping && H.shipping.threshold) || 39900;

  var reduced = window.matchMedia('(prefers-reduced-motion:reduce)').matches;
  document.documentElement.classList.add('js');

  /* ============================================================
     1. UTILITIES
     ============================================================ */

  /* Scroll lock is derived from which overlays are actually open, never from a
     counter — so it can't leak a locked <body> if two panels close out of order. */
  function updateScrollLock() {
    var open = document.querySelector(
      '.cart-drawer.is-open, .mobile-menu.is-open, .upsell-popup.is-open, ' +
      '.filter-drawer.is-open, .sizeguide.is-open, .lightbox.is-open, .search-panel.is-open'
    );
    document.body.style.overflow = open ? 'hidden' : '';
  }

  /* Money formatting, learned from the server rather than guessed.

     This used to call Intl.NumberFormat with a hardcoded 'sv-SE'. Swedish
     disambiguates the Nordic kronor, so DKK came out as "Dkr" and NOK as "Nkr"
     — the "699 Dkr" in the Danish cart. Passing the storefront locale instead
     removes that, but Intl still formats Danish as "1.198 kr." and drops the
     decimals, while the same page's server-rendered prices read "1.198,00 kr".

     So the shape is read off H.moneyProbe, which is 1 234 567,89 put through
     Liquid's own `money` filter. Whatever that market's format is, prices
     rendered here come out character-identical to the ones rendered by Liquid. */
  var _money;
  function moneyFormat() {
    if (_money !== undefined) return _money;
    var probe = String(H.moneyProbe || '');
    var first = probe.search(/\d/);
    if (first < 0) { _money = null; return _money; }
    var last = probe.length - 1;
    while (last >= 0 && !/\d/.test(probe.charAt(last))) last--;

    var body = probe.slice(first, last + 1);
    var seps = body.split(/\d+/).filter(function (s) { return s !== ''; });
    var groups = body.split(/\D+/);
    var tail = groups[groups.length - 1];

    // A two-digit final group means the last separator is the decimal point.
    // Three digits means the format carries no decimals at all.
    var decimals = (seps.length && tail.length === 2) ? 2 : 0;
    _money = {
      prefix: probe.slice(0, first),
      suffix: probe.slice(last + 1),
      decimals: decimals,
      dec: decimals ? seps[seps.length - 1] : '',
      thou: decimals ? (seps.length > 1 ? seps[0] : '') : (seps.length ? seps[0] : '')
    };
    return _money;
  }

  function money(cents) {
    var amount = (cents || 0) / 100;
    var f = moneyFormat();
    if (!f) return Math.round(amount) + ' kr';
    var parts = amount.toFixed(f.decimals).split('.');
    var whole = parts[0];
    if (f.thou) whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, f.thou);
    return f.prefix + whole + (f.decimals ? f.dec + parts[1] : '') + f.suffix;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* Shopify CDN images: swap the size segment so we never ship a 2000px file
     into a 64px slot on a 4G connection. */
  function thumb(url, w) {
    if (!url) return '';
    return url.replace(/(\.(?:png|jpe?g|webp|gif))(\?.*)?$/i, '_' + w + 'x$1$2');
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  function track(event, payload) {
    if (window.fbq) { try { window.fbq('track', event, payload); } catch (e) {} }
    if (window.gtag) { try { window.gtag('event', event, payload || {}); } catch (e) {} }
  }

  /* ============================================================
     2. SCROLL REVEAL
     ============================================================ */
  (function () {
    var items = document.querySelectorAll('.reveal-item');
    if (!items.length) return;
    if (reduced || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(items, function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var sibs = el.parentElement ? el.parentElement.querySelectorAll(':scope > .reveal-item') : [el];
        var idx = Array.prototype.indexOf.call(sibs, el);
        el.style.transitionDelay = Math.min(idx < 0 ? 0 : idx, 6) * 65 + 'ms';
        el.classList.add('is-visible');
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.04 });
    Array.prototype.forEach.call(items, function (el) { io.observe(el); });

    // Safety net: anything still hidden near the viewport after 2.5 s gets shown,
    // so a zero-height or display:none-at-load edge case can never blank a section.
    setTimeout(function () {
      Array.prototype.forEach.call(document.querySelectorAll('.reveal-item:not(.is-visible)'), function (el) {
        if (el.getBoundingClientRect().top < window.innerHeight + 250) el.classList.add('is-visible');
      });
    }, 2500);
  })();

  /* ============================================================
     3. HEADER + MOBILE NAV
     ============================================================ */
  var header = document.querySelector('.site-header');
  if (header) {
    var onScroll = function () { header.classList.toggle('scrolled', window.scrollY > 40); };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  var mobileMenu = document.querySelector('.mobile-menu');
  var menuOverlay = document.querySelector('.mobile-menu__overlay');

  function openMenu() {
    if (!mobileMenu) return;
    mobileMenu.classList.add('is-open');
    if (menuOverlay) menuOverlay.classList.add('is-active');
    mobileMenu.setAttribute('aria-hidden', 'false');
    document.querySelectorAll('[data-menu-open]').forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
    updateScrollLock();
  }
  function closeMenu() {
    if (!mobileMenu || !mobileMenu.classList.contains('is-open')) return;
    mobileMenu.classList.remove('is-open');
    if (menuOverlay) menuOverlay.classList.remove('is-active');
    mobileMenu.setAttribute('aria-hidden', 'true');
    document.querySelectorAll('[data-menu-open]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    updateScrollLock();
  }
  if (mobileMenu) {
    mobileMenu.querySelectorAll('.mobile-menu__nav a').forEach(function (a) {
      a.addEventListener('click', closeMenu);
    });
  }

  /* ============================================================
     4. ANNOUNCEMENT ROTATOR
     ============================================================ */
  (function () {
    var bar = document.querySelector('[data-announcement]');
    if (!bar) return;
    var slides = bar.querySelectorAll('.announcement__slide');
    if (slides.length < 2) return;
    var i = 0, timer = null;
    function show(n) {
      i = (n + slides.length) % slides.length;
      slides.forEach(function (s, idx) { s.classList.toggle('is-active', idx === i); });
    }
    function start() { if (!reduced) timer = setInterval(function () { show(i + 1); }, 4200); }
    function stop() { clearInterval(timer); }
    bar.addEventListener('click', function (e) {
      var prev = e.target.closest('[data-ann-prev]');
      var next = e.target.closest('[data-ann-next]');
      if (!prev && !next) return;
      stop(); show(i + (next ? 1 : -1)); start();
    });
    // Pause while the tab is hidden so we don't burn cycles in the background
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop(); else { stop(); start(); }
    });
    show(0); start();
  })();

  /* ============================================================
     5. ACCORDIONS (delegated — works for markup injected later)
     ============================================================ */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.accordion__trigger');
    if (!btn) return;
    var expanded = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', String(!expanded));
    var body = btn.nextElementSibling;
    if (body) body.hidden = expanded;
  });

  /* ============================================================
     6. CART DRAWER
     ============================================================ */
  var drawer = document.querySelector('.cart-drawer');
  var cartOverlay = document.querySelector('.cart-overlay');
  var lastCart = null;

  function openCart() {
    if (!drawer) return;
    closeMenu();
    drawer.classList.add('is-open');
    if (cartOverlay) cartOverlay.classList.add('is-active');
    drawer.setAttribute('aria-hidden', 'false');
    updateScrollLock();
  }
  function closeCart() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    if (cartOverlay) cartOverlay.classList.remove('is-active');
    drawer.setAttribute('aria-hidden', 'true');
    updateScrollLock();
  }

  /* Fills the runtime value into a locale string. Liquid can't know the cart
     total, so the `t` filter is handed a placeholder and we swap it here. */
  function fill(str, token, value) {
    return String(str || '').split(token).join(value);
  }

  /* Free-shipping meter copy. Warm and encouraging rather than pushy — it reads as
     the shop being generous, which is what actually moves basket size. */
  function shippingMessage(total) {
    if (total >= SHIP_THRESHOLD) return STR.shipUnlocked || '';
    if (total === 0) return fill(STR.shipOver, '__AMT__', '<b>' + money(SHIP_THRESHOLD) + '</b>');
    var left = '<b>' + money(SHIP_THRESHOLD - total) + '</b>';
    var pct = (total / SHIP_THRESHOLD) * 100;
    return pct >= 70
      ? fill(STR.shipAlmost, '__AMT__', left)
      : fill(STR.shipRemaining, '__AMT__', left);
  }

  function renderShipMeter(scope, total) {
    if (!scope) return;
    var wrap = scope.querySelector('.cart-ship');
    var bar = scope.querySelector('.cart-ship-bar i');
    // Explicit hook, not "the span inside the note" — the icon is a span too, and
    // the loose selector matched it first, painting the message into a 15px box.
    var note = scope.querySelector('[data-ship-text]');
    var pct = Math.min(100, (total / SHIP_THRESHOLD) * 100);
    if (bar) bar.style.width = pct + '%';
    if (note) note.innerHTML = shippingMessage(total);
    if (wrap) wrap.classList.toggle('is-complete', total >= SHIP_THRESHOLD);
  }

  /* The visible line-item properties of a cart line, in cart order. Shopify
     hides keys beginning with an underscore, so honour that convention here. */
  function visibleProps(item) {
    var out = [];
    if (!item || !item.properties) return out;
    Object.keys(item.properties).forEach(function (k) {
      var v = item.properties[k];
      if (!v || k.charAt(0) === '_') return;
      out.push({ name: k, value: String(v) });
    });
    return out;
  }

  /* Mirrors snippets/cart-line-details.liquid: drop any option value the line's
     own properties already spell out. A ring set picks its colour per ring, so
     Shopify's variant title says "2 ringar / Guld" — one colour, chosen more or
     less arbitrarily — while the properties underneath say silver and rose gold.
     Shared by the drawer and the add-to-cart popup so both say the same thing;
     the popup used to print the raw title and contradict itself. */
  function variantLabel(variantTitle, props) {
    var vt = (variantTitle && variantTitle !== 'Default Title') ? String(variantTitle) : '';
    if (!vt || !props || !props.length) return vt;
    var blob = props.map(function (p) { return p.value; }).join(' ').toLowerCase();
    // Three characters minimum — a size called "S" matches a letter inside
    // almost any engraving, and we would silently drop a real size.
    return vt.split(' / ').filter(function (part) {
      var needle = part.trim().toLowerCase();
      return needle.length < 3 || blob.indexOf(needle) === -1;
    }).join(' / ');
  }

  function renderCart(cart) {
    if (!cart || typeof cart.item_count === 'undefined') return;
    lastCart = cart;

    document.querySelectorAll('.cart-count').forEach(function (b) {
      b.textContent = cart.item_count;
      b.style.display = cart.item_count > 0 ? 'flex' : 'none';
    });
    if (!drawer) return;

    var itemsEl = drawer.querySelector('.cart-drawer__items');
    var footEl = drawer.querySelector('.cart-drawer__foot');
    var upsellEl = drawer.querySelector('.cart-upsell');
    var countEl = drawer.querySelector('[data-cart-headcount]');
    if (countEl) countEl.textContent = cart.item_count ? '(' + cart.item_count + ')' : '';
    if (!itemsEl) return;

    if (!cart.items || !cart.items.length) {
      itemsEl.innerHTML =
        '<div class="cart-empty">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" aria-hidden="true">' +
            '<path d="M5 8h14l-1.2 11H6.2L5 8Z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/></svg>' +
          '<p>' + esc(STR.cartEmpty || '') + '</p>' +
          '<a class="btn" href="' + ROOT + 'collections/all"><span>' + esc(STR.continueShopping || '') + '</span></a>' +
        '</div>';
      if (footEl) footEl.style.display = 'none';
      if (upsellEl) upsellEl.style.display = 'none';
      renderShipMeter(drawer, 0);
      return;
    }

    if (footEl) footEl.style.display = '';

    var html = '';
    cart.items.forEach(function (item) {
      var img = item.image ? thumb(item.image, 140) : '';
      var onSale = item.original_line_price > item.final_line_price;

      var props = visibleProps(item);
      var variant = variantLabel(item.variant_title, props);
      var propsHtml = props.map(function (p) {
        return '<div class="cart-line__prop">' + esc(p.name) + ': <b>' + esc(p.value) + '</b></div>';
      }).join('');

      html +=
        '<div class="cart-line" data-key="' + esc(item.key) + '">' +
          (img
            ? '<img src="' + esc(img) + '" alt="" width="64" height="74" loading="lazy">'
            : '<span></span>') +
          '<div>' +
            '<div class="cart-line__title">' + esc(item.product_title) + '</div>' +
            (variant ? '<div class="cart-line__variant">' + esc(variant) + '</div>' : '') +
            propsHtml +
            '<div class="cart-line__qty">' +
              '<button type="button" data-qty-down aria-label="' + esc(STR.qtyDown || '−') + '">−</button>' +
              '<span>' + item.quantity + '</span>' +
              '<button type="button" data-qty-up aria-label="' + esc(STR.qtyUp || '+') + '">+</button>' +
            '</div>' +
          '</div>' +
          '<div>' +
            '<div class="cart-line__price">' +
              (onSale ? '<s style="opacity:.45;font-weight:300;display:block;font-size:11.5px">' + money(item.original_line_price) + '</s>' : '') +
              money(item.final_line_price) +
            '</div>' +
            '<button type="button" class="cart-line__remove" data-remove>' + esc(STR.remove || '') + '</button>' +
          '</div>' +
        '</div>';
    });
    itemsEl.innerHTML = html;

    renderShipMeter(drawer, cart.total_price);

    var totalEl = drawer.querySelector('[data-cart-total]');
    if (totalEl) totalEl.textContent = money(cart.total_price);

    loadDrawerUpsell(cart);
  }

  /* In-drawer recommendation rail, keyed to the first item in the cart.
     Cached per product id so opening/closing the drawer doesn't re-fetch. */
  var drawerUpsellCache = {};
  function loadDrawerUpsell(cart) {
    var el = drawer && drawer.querySelector('.cart-upsell');
    if (!el || !cart.items || !cart.items.length) return;
    var pid = cart.items[0].product_id;
    var inCart = {};
    cart.items.forEach(function (it) { inCart[it.product_id] = true; });

    function paint(products) {
      // Four. The drawer's job is to show the shopper what they are buying; the
      // rail is a nudge alongside it, so it stays one short scrollable strip
      // rather than a second shop competing with the basket.
      var available = products.filter(function (p) { return !inCart[p.id]; }).slice(0, 4);
      if (!available.length) { el.style.display = 'none'; return; }
      el.style.display = '';
      // Always a link, never a one-tap add. Our pieces carry engraving, colour
      // and size choices, and quietly adding one with none of them chosen puts
      // an unfulfillable line in the cart — the shopper finds out at the packing
      // table, not the checkout.
      el.querySelector('.cart-upsell__rail').innerHTML = available.map(function (p) {
        return '<a class="cu" href="' + esc(p.url) + '">' +
          '<img src="' + esc(p.image) + '" alt="' + esc(p.title) + '" loading="lazy" width="42" height="42">' +
          '<span class="cu__info">' +
            '<strong>' + esc(p.title) + '</strong>' +
            '<span>' + money(p.price) + '</span>' +
          '</span>' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9.5 5.5 16 12 9.5 18.5"/></svg>' +
        '</a>';
      }).join('');
    }

    if (drawerUpsellCache[pid]) { paint(drawerUpsellCache[pid]); return; }
    fetchRecommendations(pid, 8).then(function (products) {
      drawerUpsellCache[pid] = products;
      paint(products);
    });
  }

  function fetchCart() {
    return fetch(ROOT + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(renderCart)
      .catch(function () {});
  }

  function changeQty(key, quantity) {
    return postJSON(ROUTES.cart_change_url || (ROOT + 'cart/change.js'), { id: key, quantity: quantity })
      .then(renderCart)
      .catch(function () {});
  }

  if (drawer) {
    drawer.addEventListener('click', function (e) {
      var line = e.target.closest('.cart-line');
      if (!line) return;
      var key = line.getAttribute('data-key');
      var qtyEl = line.querySelector('.cart-line__qty span');
      var qty = qtyEl ? parseInt(qtyEl.textContent, 10) : 1;
      if (e.target.closest('[data-qty-up]')) changeQty(key, qty + 1);
      else if (e.target.closest('[data-qty-down]')) changeQty(key, qty - 1);
      else if (e.target.closest('[data-remove]')) changeQty(key, 0);
    });
  }

  /* ============================================================
     7. ADD TO CART (AJAX)
     ============================================================ */
  function addToCart(items, btn, opts) {
    opts = opts || {};
    var original = btn ? btn.innerHTML : null;
    if (btn) { btn.disabled = true; btn.innerHTML = '<span>…</span>'; }

    return postJSON(ROUTES.cart_add_url || (ROOT + 'cart/add.js'), { items: items })
      .then(function (data) {
        if (data && data.status && data.status >= 400) throw new Error(data.description || 'cart error');
        if (btn) btn.innerHTML = '<span>' + esc(STR.addedToCart || 'Tillagd') + ' ✓</span>';

        var added = (data && data.items && data.items[0]) || data;
        track('AddToCart', added ? {
          content_ids: [String(added.variant_id || added.id)],
          content_name: added.product_title || added.title,
          content_type: 'product',
          value: (added.final_line_price || added.price || 0) / 100,
          currency: H.currency || 'SEK'
        } : {});

        return fetchCart().then(function () {
          var pid = added && (added.product_id || added.id);
          if (opts.showUpsell !== false && pid) showUpsell(pid, added);
          else if (opts.openCartAfter !== false) openCart();
        });
      })
      .catch(function () {
        if (btn) btn.innerHTML = '<span>' + esc(STR.addFailed || '') + '</span>';
      })
      .then(function () {
        setTimeout(function () {
          if (btn) { btn.disabled = false; btn.innerHTML = original; }
        }, 1300);
      });
  }
  window.LorenzaAddToCart = addToCart;
  window.LorenzaOpenCart = openCart;

  /* Collect properties[...] inputs into the object /cart/add.js expects, so
     engraving travels with the line item — onto the order, packing slip and
     confirmation email — without any personalisation app. */
  function collectProperties(form) {
    var props = null;
    form.querySelectorAll('[name^="properties["]').forEach(function (input) {
      // Disabled inputs are the rings a smaller bundle doesn't include — the
      // markup renders every ring once and switches the extras off.
      if (input.disabled) return;
      if (input.type === 'checkbox' && !input.checked) return;
      var m = input.name.match(/^properties\[(.+)\]$/);
      if (!m) return;
      var val = (input.value || '').trim();
      if (!val) return;
      props = props || {};
      props[m[1]] = val;
    });
    return props;
  }

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[data-ajax-cart]');
    if (!form) return;
    e.preventDefault();
    var idField = form.querySelector('[name="id"]');
    if (!idField || !idField.value) return;

    // Every ring in the chosen bundle must have both letters before we let the
    // order through — an engraved item can't be reworked after the fact.
    if (form.querySelector('[data-engrave]') && window.LorenzaEngraveCheck && !window.LorenzaEngraveCheck()) return;

    var qtyInput = form.querySelector('[name="quantity"]');
    var qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
    var item = { id: parseInt(idField.value, 10), quantity: qty };
    var props = collectProperties(form);
    if (props) item.properties = props;

    var items = [item];

    // Gift box rides along as a second line item — one per ring, so a 2-ring set
    // arrives with two boxes rather than one couple's worth split between them.
    var gift = document.querySelector('[data-giftwrap] input:checked');
    if (gift && gift.value) {
      var giftQty = window.LorenzaGiftQty ? window.LorenzaGiftQty() : 1;
      items.push({ id: parseInt(gift.value, 10), quantity: Math.max(1, giftQty) });
    }

    var fromUpsell = !!form.closest('[data-upsell-popup]');
    addToCart(items, form.querySelector('[type="submit"]'), {
      showUpsell: !fromUpsell,
      openCartAfter: !fromUpsell
    });
  });

  /* ============================================================
     7b. RING BUNDLE + PER-RING PERSONALISATION
     Owns the variant, the price and the engraving on the ring template.
     One card is rendered per ring in the largest bundle; picking a smaller tier
     hides the surplus cards and disables their hidden inputs, so exactly the
     rings being bought reach /cart/add.js as line-item properties.
     ============================================================ */
  (function () {
    var engrave = document.querySelector('[data-engrave]');
    if (!engrave) return;
    var form = engrave.closest('form[data-product-form]');
    if (!form) return;

    var bundle = document.querySelector('[data-bundle]');
    var cards = Array.prototype.slice.call(engrave.querySelectorAll('[data-ring-card]'));
    if (!cards.length) return;

    var tiers = bundle ? Array.prototype.slice.call(bundle.querySelectorAll('[data-tier]')) : [];
    var matchToggle = engrave.querySelector('[data-match-toggle]');
    var errEl = engrave.querySelector('[data-engrave-error]');
    var idField = form.querySelector('[data-variant-field]') || form.querySelector('[name="id"]');
    var atcTotal = form.querySelector('[data-atc-total]');

    var activeRings = 1;

    function inputsOf(card) {
      return Array.prototype.slice.call(card.querySelectorAll('[data-engrave-input]'));
    }
    function lettersOf(card) {
      var ins = inputsOf(card);
      return {
        l: (ins[0] && ins[0].value.trim().toUpperCase()) || '',
        r: (ins[1] && ins[1].value.trim().toUpperCase()) || ''
      };
    }
    function colourOf(card) {
      var on = card.querySelector('.ringseg__btn.is-active');
      return on ? on.getAttribute('data-colour') : '';
    }
    function matched() { return !!(matchToggle && matchToggle.checked); }

    function complete() {
      for (var n = 1; n <= activeRings; n++) {
        var lt = (matched() && n > 1) ? lettersOf(cards[0]) : lettersOf(cards[n - 1]);
        if (!lt.l || !lt.r) return false;
      }
      return true;
    }

    function sync() {
      var first = lettersOf(cards[0]);
      var firstTxt = (first.l || 'A') + ' & ' + (first.r || 'B');

      cards.forEach(function (card, i) {
        var n = i + 1;
        var on = n <= activeRings;
        card.classList.toggle('is-hidden', !on);

        // Rings 2+ mirror ring 1 while "same initials" is on, so the couple case
        // is two inputs rather than four.
        var mirror = matched() && n > 1;
        var fields = card.querySelector('[data-ring-fields]');
        var mirrorEl = card.querySelector('[data-ring-mirror]');
        if (fields) fields.hidden = mirror;
        if (mirrorEl) mirrorEl.hidden = !mirror;

        // Only copy into rings that are actually part of the chosen bundle. Left
        // unguarded, a hidden ring keeps stale letters and then sails through
        // validation when the shopper later enlarges the bundle.
        if (mirror && on) {
          var ins = inputsOf(card);
          if (ins[0]) ins[0].value = first.l;
          if (ins[1]) ins[1].value = first.r;
        }

        var lt = (mirror && on) ? first : lettersOf(card);
        var filled = !!(lt.l && lt.r);
        var txt = (lt.l || 'A') + ' & ' + (lt.r || 'B');

        var out = card.querySelector('[data-engrave-out]');
        if (out) out.textContent = txt;
        var mout = card.querySelector('[data-mirror-out]');
        if (mout) mout.textContent = firstTxt;

        var colour = colourOf(card);
        var summary = card.querySelector('[data-ring-summary]');
        if (summary) summary.textContent = colour;

        // The property only exists once real letters do — never the A/B placeholder,
        // which would otherwise reach the order if a submit ever slipped past.
        var prop = card.querySelector('[data-ring-prop]');
        if (prop) {
          if (on && filled) {
            prop.disabled = false;
            // One key shape at every tier. A single ring used to write "Din ring"
            // while a pair wrote "Ring 1" / "Ring 2", so fulfilment had to branch on
            // how many rings were bought just to read the engraving. Numbered always:
            // a single ring is "Ring 1".
            prop.name = 'properties[' + fill(STR.propertyNumbered, '__N__', n) + ']';
            // Colour first, then the letters. The supplier picks the physical ring off
            // the shelf by colour and only then engraves it, so the value reads in their
            // order of operations at the bench.
            prop.value = colour ? colour + ' · ' + txt : txt;
          } else {
            prop.disabled = true;
            prop.removeAttribute('name');
          }
        }
      });

      if (complete()) {
        engrave.classList.remove('is-invalid');
        if (errEl) errEl.hidden = true;
      }
    }

    function selectTier(radio) {
      if (!radio || !bundle) return;
      activeRings = parseInt(radio.getAttribute('data-units'), 10) || 1;

      var vid = radio.getAttribute('data-vid');
      if (idField && vid) idField.value = vid;

      // The total lives on the main button only. The headline price and the
      // sticky bar both stay on the single-ring figure people saw in the ad.
      var price = parseInt(radio.getAttribute('data-price'), 10) || 0;
      if (atcTotal) atcTotal.textContent = ' · ' + money(price);

      Array.prototype.forEach.call(bundle.querySelectorAll('.bundle__tier'), function (t) {
        t.classList.toggle('is-selected', t.contains(radio));
      });
      sync();
    }

    /* One gift box per ring. The card shows a fixed per-box price with "per
       ring" underneath, so nothing on screen has to move when the tier changes —
       only the quantity that reaches the cart does. */
    window.LorenzaGiftQty = function () { return activeRings; };

    if (bundle) {
      bundle.addEventListener('change', function (e) {
        var r = e.target.closest('[data-tier]');
        if (r) selectTier(r);
      });
    }

    engrave.addEventListener('click', function (e) {
      var btn = e.target.closest('.ringseg__btn');
      if (!btn) return;
      e.preventDefault();
      Array.prototype.forEach.call(btn.parentElement.querySelectorAll('.ringseg__btn'), function (b) {
        b.classList.remove('is-active');
        b.setAttribute('aria-pressed', 'false');
      });
      btn.classList.add('is-active');
      btn.setAttribute('aria-pressed', 'true');
      sync();
    });

    engrave.addEventListener('input', function (e) {
      var input = e.target.closest('[data-engrave-input]');
      if (!input) return;
      var raw = input.value;

      // A-Z only. The old filter allowed any Unicode letter, so A-ring, A-umlaut
      // and O-umlaut passed validation and reached the supplier, whose machine
      // cannot cut them. An engraved ring is the one thing on this page that can
      // never be returned, so letting one through costs a replacement.
      var v = raw.replace(/[^A-Za-z]/g, '').toUpperCase().slice(0, 1);
      if (v !== raw) input.value = v;

      // Explain the drop, but only when what we dropped was a letter. A digit or a
      // symbol is self-evidently not engravable; a rejected A-umlaut just looks
      // like a broken field unless we say why. Any valid keystroke clears it.
      var card = input.closest('[data-ring-card]');
      var note = card && card.querySelector('[data-engrave-note]');
      var dropped = raw.replace(/[A-Za-z]/g, '');
      // A character whose upper and lower case differ is a letter - true of A-ring,
      // A-umlaut and O-umlaut, false of digits and punctuation. Avoids a Unicode
      // property escape and needs no /u flag.
      var droppedLetter = dropped !== dropped.toUpperCase() || dropped !== dropped.toLowerCase();
      if (note) note.hidden = !droppedLetter;
      if (v) {
        var ins = inputsOf(card);
        var idx = ins.indexOf(input);
        if (ins[idx + 1] && !ins[idx + 1].value) ins[idx + 1].focus();
      }
      sync();
    });

    engrave.addEventListener('focusin', function (e) {
      if (e.target.matches && e.target.matches('[data-engrave-input]')) e.target.select();
    });

    if (matchToggle) matchToggle.addEventListener('change', function () {
      engrave.classList.toggle('is-matched', matchToggle.checked);
      sync();
    });

    function firstEmpty() {
      for (var n = 1; n <= activeRings; n++) {
        if (matched() && n > 1) continue;
        var ins = inputsOf(cards[n - 1]);
        for (var i = 0; i < ins.length; i++) {
          if (!ins[i].value.trim()) return ins[i];
        }
      }
      return null;
    }

    /* Called by the cart submit handler before anything is posted. */
    window.LorenzaEngraveCheck = function () {
      if (complete()) return true;
      engrave.classList.add('is-invalid');
      if (errEl) errEl.hidden = false;
      engrave.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
      var target = firstEmpty();
      if (target) target.focus({ preventScroll: true });
      return false;
    };

    /* Read-only — lets the sticky bar decide whether to submit or send the
       shopper up to the bundle, without flashing an error at them first. */
    window.LorenzaEngraveReady = complete;

    /* The sticky bar's "Köp" lands here when the engraving isn't filled in yet.
       Scrolls to the bundle (2 rings already selected) rather than the engraving,
       so the shopper sees what they're choosing before they're asked to type. */
    window.LorenzaFocusBundle = function () {
      var target = bundle || engrave;
      target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      engrave.classList.add('is-invalid');
      if (errEl) errEl.hidden = false;
      var input = firstEmpty();
      // preventScroll, or focusing would yank the page past the bundle we just
      // scrolled to — on mobile it also pops the keyboard over the tiers.
      if (input) setTimeout(function () { input.focus({ preventScroll: true }); }, reduced ? 0 : 500);
    };

    if (matchToggle) engrave.classList.toggle('is-matched', matchToggle.checked);

    var pre = tiers.filter(function (t) { return t.checked && !t.disabled; })[0]
           || tiers.filter(function (t) { return !t.disabled; })[0];
    if (pre) { pre.checked = true; selectTier(pre); } else { sync(); }
  })();

  /* ============================================================
     7c. NECKLACE — colour picker + optional bundle
     Colour is a real variant axis on this page, so picking one is a variant
     change: the id, price, struck price, sticky bar and the gallery photo all
     follow. The bundle tiers are optional - the live product has none - and
     when they are present every figure on the cards is recomputed from the
     product JSON by the same rules the Liquid used server-side.

     This block used to return early when there was no bundle, which is why
     the colour buttons on the live page did nothing. The picker now runs on
     its own and the bundle is layered on top when it exists.
     ============================================================ */
  (function () {
    var form = document.querySelector('form[data-product-form]');
    if (!form) return;
    var mats = form.querySelector('[data-nk-materials]');
    var bundle = form.querySelector('[data-nbundle]');
    if (!mats && !bundle) return;

    var jsonEl = document.querySelector('[data-product-json]');
    var product = null;
    try { product = JSON.parse(jsonEl.textContent); } catch (e) { return; }
    if (!product || !product.variants || !product.variants.length) return;

    /* Sentences, not fragments: the locale file decides where the amount sits in
       the sentence, and half these languages do not put it where Swedish does. */
    var NKS = { each: '__P__', save: '__A__' };
    var strEl = document.querySelector('[data-necklace-strings]');
    if (strEl) { try { NKS = JSON.parse(strEl.textContent); } catch (e) {} }

    var qidx = bundle ? (parseInt(bundle.getAttribute('data-qidx'), 10) || 0) : -1;
    var midx = parseInt((mats || bundle).getAttribute('data-midx'), 10);
    if (isNaN(midx)) midx = -1;

    var tiers = bundle ? [].slice.call(bundle.querySelectorAll('[data-tier]')) : [];
    var matBtns = mats ? [].slice.call(mats.querySelectorAll('[data-nk-material]')) : [];
    var matName = mats ? mats.querySelector('[data-nk-material-name]') : null;
    var idField = form.querySelector('[data-variant-field]') || form.querySelector('[name="id"]');
    var submitBtn = form.querySelector('[type="submit"]');
    var submitSpan = submitBtn ? submitBtn.querySelector('span') : null;
    var atcTotal = form.querySelector('[data-atc-total]');
    var priceEl = document.querySelector('[data-price]');
    var compareEl = document.querySelector('[data-compare-price]');
    var discountEl = document.querySelector('[data-discount]');
    var stickyPrice = document.querySelector('.sticky-atc__price');
    var stickyWas = document.querySelector('[data-sticky-compare]');
    var stickyImg = document.querySelector('[data-sticky-img]');
    var stickyBtn = document.querySelector('[data-sticky-add]');

    // The button label is a text node followed by the total <em>. Keep a handle
    // on the node so "Slutsåld" can replace the label without destroying the em.
    var labelNode = null, baseLabel = '';
    if (submitSpan) {
      labelNode = submitSpan.firstChild;
      if (!labelNode || labelNode.nodeType !== 3) {
        labelNode = document.createTextNode('');
        submitSpan.insertBefore(labelNode, submitSpan.firstChild);
      }
      baseLabel = labelNode.nodeValue;
    }

    var material = '';
    var activeUnits = 1;

    function readMaterial() {
      var on = matBtns.filter(function (b) { return b.classList.contains('is-active'); })[0];
      return on ? on.getAttribute('data-value') : '';
    }
    material = readMaterial();

    /* The variant for a quantity value under the current colour. With no bundle
       the quantity is ignored and the colour alone picks the variant. */
    function variantFor(qvalue) {
      for (var i = 0; i < product.variants.length; i++) {
        var v = product.variants[i];
        if (qidx >= 0 && qvalue !== null && v.options[qidx] !== qvalue) continue;
        if (midx >= 0 && material && v.options[midx] !== material) continue;
        return v;
      }
      return null;
    }

    /* The single-piece variant is the anchor: it sets the headline price and the
       per-piece baseline every saving on the page is measured against. Same rule
       as the Liquid, so a colour switch cannot drift from first paint. */
    function anchor() {
      if (!bundle) return variantFor(null);
      for (var i = 0; i < tiers.length; i++) {
        if (parseInt(tiers[i].getAttribute('data-units'), 10) === 1) {
          return variantFor(tiers[i].getAttribute('data-value'));
        }
      }
      return variantFor(null);
    }

    function paintHeadline(v) {
      if (!v) return;
      var compare = v.compare_at_price || 0;
      var onSale = compare > v.price;
      if (priceEl) priceEl.textContent = money(v.price);
      if (stickyPrice) stickyPrice.textContent = money(v.price);
      if (compareEl) { compareEl.hidden = !onSale; if (onSale) compareEl.textContent = money(compare); }
      if (stickyWas) { stickyWas.hidden = !onSale; if (onSale) stickyWas.textContent = money(compare); }
      if (discountEl) {
        var off = onSale ? Math.round((compare - v.price) * 100 / compare) : 0;
        discountEl.hidden = !off;
        if (off) discountEl.textContent = '−' + off + '%';
      }
    }

    /* The gallery follows the colour. A variant with no photo of its own leaves
       the gallery where it is rather than jumping to photo 1 - that is the
       merchant forgetting to assign an image, not a reason to show the wrong
       colour. LorenzaShowSlide is defined by section 10, which runs after this
       block, so it is looked up at call time. */
    function showVariantImage(v) {
      if (!v || !v.featured_image || !v.featured_image.position) return;
      if (window.LorenzaShowSlide) window.LorenzaShowSlide(v.featured_image.position - 1);
      if (stickyImg && v.featured_image.src) stickyImg.src = v.featured_image.src;
    }

    function paintAvailability(v) {
      var ok = !!(v && v.available);
      if (submitBtn) submitBtn.disabled = !ok;
      if (labelNode) labelNode.nodeValue = ok ? baseLabel : (STR.soldOut || baseLabel);
      if (atcTotal) atcTotal.hidden = !ok;
      if (stickyBtn) {
        stickyBtn.disabled = !ok;
        var s = stickyBtn.querySelector('span');
        if (s) s.textContent = ok ? (STR.buy || s.textContent) : (STR.soldOutShort || s.textContent);
      }
    }

    function refresh() {
      var base = anchor();
      paintHeadline(base);

      if (!bundle) {
        if (base && idField) idField.value = base.id;
        paintAvailability(base);
        activeUnits = 1;
        return;
      }

      var basePrice = base ? base.price : 0;
      var baseCompare = base ? (base.compare_at_price || 0) : 0;
      var unitCompare = baseCompare > basePrice ? baseCompare : basePrice;

      tiers.forEach(function (radio) {
        var units = parseInt(radio.getAttribute('data-units'), 10) || 1;
        var card = radio.closest('.bundle__tier');
        var v = variantFor(radio.getAttribute('data-value'));
        if (!card) return;

        if (!v) { card.classList.add('is-oos'); radio.disabled = true; return; }

        radio.setAttribute('data-vid', v.id);
        radio.setAttribute('data-price', v.price);
        radio.disabled = !v.available;
        card.classList.toggle('is-oos', !v.available);

        var full = unitCompare * units;
        if ((v.compare_at_price || 0) > full) full = v.compare_at_price;
        var saving = full - v.price;
        // Rounded, not truncated: at 2 pieces the true saving is 16.6% and a
        // shopper checking the card against the sum in their head expects 17%.
        var pct = (full > 0 && saving > 0) ? Math.round(saving * 100 / full) : 0;
        var each = Math.round(v.price / units);
        radio.setAttribute('data-save', saving);

        var totalEl = card.querySelector('[data-tier-total]');
        if (totalEl) totalEl.textContent = money(v.price);
        var fullEl = card.querySelector('[data-tier-full]');
        if (fullEl) { fullEl.hidden = saving <= 0; if (saving > 0) fullEl.textContent = money(full); }
        var eachEl = card.querySelector('[data-tier-each]');
        if (eachEl) eachEl.textContent = fill(NKS.each, '__P__', money(each));
        var saveEl = card.querySelector('[data-tier-save]');
        if (saveEl) {
          saveEl.hidden = saving <= 0;
          if (saving > 0) saveEl.textContent = fill(fill(NKS.save, '__A__', money(saving)), '__N__', pct);
        }
      });

      var checked = tiers.filter(function (t) { return t.checked && !t.disabled; })[0]
                 || tiers.filter(function (t) { return !t.disabled; })[0];
      if (checked) { checked.checked = true; selectTier(checked); }
      else paintAvailability(null);
    }

    function selectTier(radio) {
      if (!radio) return;
      activeUnits = parseInt(radio.getAttribute('data-units'), 10) || 1;
      var vid = radio.getAttribute('data-vid');
      if (idField && vid) idField.value = vid;

      // The total lives on the buy button only. The headline price and the
      // sticky bar both stay on the single-piece figure people saw in the ad.
      var price = parseInt(radio.getAttribute('data-price'), 10) || 0;
      if (atcTotal) atcTotal.textContent = ' · ' + money(price);
      paintAvailability({ available: !radio.disabled });

      [].forEach.call(bundle.querySelectorAll('.bundle__tier'), function (t) {
        t.classList.toggle('is-selected', t.contains(radio));
      });
    }

    if (bundle) {
      bundle.addEventListener('change', function (e) {
        var r = e.target.closest('[data-tier]');
        if (r) selectTier(r);
      });
    }

    if (mats) {
      mats.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-nk-material]');
        if (!btn) return;
        e.preventDefault();
        matBtns.forEach(function (b) {
          var on = b === btn;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        material = readMaterial();
        if (matName) matName.textContent = material;
        refresh();
        showVariantImage(anchor());
      });
    }

    /* One gift box per piece, so the quantity follows the tier while the price
       on the card stays still. Same contract the ring page uses. */
    window.LorenzaGiftQty = function () { return activeUnits; };

    /* Section 13 asks this before the sticky button buys. With a bundle on the
       page the bar shows the single-piece price, so it must not add a 2-piece
       bundle at a price the shopper has not seen - it sends them to the tiers
       instead. With no bundle the price on the bar is the price they pay, and
       the tap adds the chosen colour (and the gift box, if ticked). */
    window.LorenzaEngraveReady = function () { return !bundle; };
    window.LorenzaFocusBundle = function () {
      if (!bundle) return;
      bundle.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
      bundle.classList.add('is-pulsed');
      setTimeout(function () { bundle.classList.remove('is-pulsed'); }, 1200);
    };

    refresh();
  })();

  /* Delegated quick-add — product cards only. The drawer rail and the post-add
     popup deliberately link through to the product instead, so nothing that
     needs an engraving or a size can be added without one. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    var id = parseInt(btn.getAttribute('data-quick-add'), 10);
    if (!id) return;
    var insideOverlay = !!btn.closest('[data-upsell-popup], .cart-drawer');
    btn.classList.add('is-added');
    addToCart([{ id: id, quantity: 1 }], btn, {
      showUpsell: !insideOverlay,
      openCartAfter: !insideOverlay
    });
  });

  /* ============================================================
     8. POST-ADD-TO-CART UPSELL POPUP
     ============================================================ */
  var upsell = document.querySelector('[data-upsell-popup]');

  function closeUpsell() {
    if (!upsell) return;
    upsell.classList.remove('is-open');
    upsell.setAttribute('aria-hidden', 'true');
    updateScrollLock();
  }
  if (upsell) {
    upsell.addEventListener('click', function (e) {
      if (e.target.closest('[data-upsell-close]')) { closeUpsell(); return; }
      if (e.target.closest('[data-upsell-cart]')) { closeUpsell(); openCart(); return; }
      if (e.target.closest('[data-upsell-checkout]')) { window.location.href = ROUTES.cart_url || (ROOT + 'cart'); }
    });
  }

  /* Reads Shopify's own recommendations endpoint. Section rendering returns markup,
     so we parse the JSON payload the section emits — that keeps prices, currency and
     availability correct without duplicating pricing logic in JS. */
  function fetchRecommendations(productId, limit) {
    var url = ROOT + 'recommendations/products?section_id=product-recommendations&limit=' +
              (limit || 4) + '&intent=related&product_id=' + productId;
    return fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (txt) {
        var tmp = document.createElement('div');
        tmp.innerHTML = txt;
        var payload = tmp.querySelector('[data-rec-json]');
        if (!payload) return [];
        try { return JSON.parse(payload.textContent) || []; } catch (e) { return []; }
      })
      .catch(function () { return []; });
  }

  /* Server-rendered backstop. Shopify's recommendation engine needs order history
     before it says anything, so on a young store it returns an empty list — which
     would silently turn the popup off exactly when it's most valuable. */
  function fallbackRecs() {
    var el = document.querySelector('[data-upsell-fallback]');
    if (!el) return [];
    try { return JSON.parse(el.textContent) || []; } catch (e) { return []; }
  }

  function showUpsell(productId, added) {
    if (!upsell) { openCart(); return; }

    var addedTitle = upsell.querySelector('[data-upsell-title]');
    if (addedTitle && added) {
      addedTitle.textContent = added.product_title || added.title || '';
    }
    // The same filter the drawer uses. A two-ring set has a colour per ring in
    // its properties, and Shopify's variant title still names just one of them —
    // printing it raw told a shopper who picked silver and gold that they had
    // bought "2 ringar / Guld". The real choices go on the line below instead.
    var props = visibleProps(added);
    var addedVariant = upsell.querySelector('[data-upsell-variant]');
    if (addedVariant) {
      var vt = variantLabel(added && added.variant_title, props);
      // A bundle variant can be titled just "2" — the bare number reads as an
      // error rather than as two rings, so it is dropped and the tier speaks
      // for itself.
      var meaningful = vt && !/^\d+$/.test(vt.trim());
      addedVariant.textContent = meaningful ? vt : '';
      addedVariant.hidden = !meaningful;
    }

    // Engraving and per-ring colour, exactly as they will appear in the cart.
    // This is the confirmation the shopper actually wants to read back.
    var addedProps = upsell.querySelector('[data-upsell-props]');
    if (addedProps) {
      addedProps.innerHTML = props.map(function (p) {
        return '<span>' + esc(p.name) + ': <b>' + esc(p.value) + '</b></span>';
      }).join('');
      addedProps.hidden = !props.length;
    }
    renderShipMeter(upsell, lastCart ? lastCart.total_price : 0);

    var listEl = upsell.querySelector('[data-upsell-items]');

    fetchRecommendations(productId, 6).then(function (products) {
      var inCart = {};
      if (lastCart && lastCart.items) lastCart.items.forEach(function (it) { inCart[it.product_id] = true; });

      function usable(list) {
        return list.filter(function (p) {
          return p && p.available && !inCart[p.id] && p.id !== productId;
        });
      }

      // Three. The sheet has to fit a phone with its two buttons still visible,
      // and a fourth row pushes them under the fold — where the shopper scrolls
      // past the choice instead of making one. Shopify's own recommendations
      // win; the server-rendered fallback covers a store with no order history.
      var MAX_PICKS = 3;
      var picks = usable(products).slice(0, MAX_PICKS);
      if (!picks.length) picks = usable(fallbackRecs()).slice(0, MAX_PICKS);

      if (!picks.length) { openCart(); return; }

      // Link through rather than one-tap add: these pieces have engraving,
      // colour and size to choose, and an add that skips them produces an order
      // nobody can actually make.
      listEl.innerHTML = picks.map(function (p) {
        var onSale = p.compare_at_price && p.compare_at_price > p.price;
        return '<a class="up" href="' + esc(p.url) + '">' +
          '<img src="' + esc(p.image) + '" alt="' + esc(p.title) + '" width="66" height="66" loading="lazy">' +
          '<span class="up__info">' +
            '<strong>' + esc(p.title) + '</strong>' +
            '<span class="up__price">' +
              (onSale ? '<s>' + money(p.compare_at_price) + '</s>' : '') +
              money(p.price) +
            '</span>' +
          '</span>' +
          '<span class="up__go">' + esc(STR.view || '') +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9.5 5.5 16 12 9.5 18.5"/></svg>' +
          '</span>' +
        '</a>';
      }).join('');

      upsell.classList.add('is-open');
      upsell.setAttribute('aria-hidden', 'false');
      updateScrollLock();
    });
  }

  /* ============================================================
     9. GLOBAL DELEGATED TOGGLES + ESC
     ============================================================ */
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-menu-open]')) { e.preventDefault(); openMenu(); return; }
    if (e.target.closest('[data-menu-close]')) { closeMenu(); return; }
    if (e.target.closest('[data-cart-toggle]')) { e.preventDefault(); openCart(); fetchCart(); return; }
    if (e.target.closest('[data-cart-close]')) { closeCart(); return; }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    closeCart(); closeUpsell(); closeMenu(); closeSizeGuide(); closeLightbox(); closeSearch();
    var fd = document.querySelector('.filter-drawer.is-open');
    if (fd) {
      fd.classList.remove('is-open');
      fd.setAttribute('aria-hidden', 'true');
      var pill = document.querySelector('[data-filter-pill]');
      if (pill) pill.classList.remove('is-hidden');
    }
    updateScrollLock();
  });

  /* ============================================================
     10. PDP GALLERY
     ============================================================ */
  (function () {
    var wrap = document.querySelector('.pdp__main-img');
    if (!wrap) return;
    var slides = wrap.querySelectorAll('.pdp__slide');
    if (!slides.length) return;
    var thumbStrip = document.querySelector('.pdp__thumbs');
    var thumbs = document.querySelectorAll('.pdp__thumbs button[data-index]');
    // Only the standard product template still renders dots; the ring template
    // relies on arrows plus the thumbnail strip.
    var dots = document.querySelectorAll('.pdp__dots button');
    var counterEl = document.querySelector('[data-gallery-counter] b');
    var current = 0;

    function setSlide(i) {
      var n = slides.length;
      current = ((i % n) + n) % n;
      slides.forEach(function (s, idx) { s.classList.toggle('is-active', idx === current); });
      thumbs.forEach(function (t) {
        t.classList.toggle('is-active', (parseInt(t.getAttribute('data-index'), 10) || 0) === current);
      });
      dots.forEach(function (d, idx) { d.classList.toggle('is-active', idx === current); });
      if (counterEl) counterEl.textContent = current + 1;
      // Keep the active thumb in view once the strip has been expanded
      if (thumbStrip && thumbStrip.scrollWidth > thumbStrip.clientWidth) {
        var active = thumbStrip.querySelector('button.is-active');
        if (active && active.scrollIntoView) {
          active.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
        }
      }
    }
    window.LorenzaShowSlide = setSlide;

    thumbs.forEach(function (t) {
      t.addEventListener('click', function () { setSlide(parseInt(t.getAttribute('data-index'), 10) || 0); });
    });
    dots.forEach(function (d, idx) { d.addEventListener('click', function () { setSlide(idx); }); });

    /* "+N" tile: reveal the rest of the strip in place and jump to the photo the
       tile was showing, so the tap lands somewhere rather than just reflowing. */
    var expand = document.querySelector('[data-thumb-expand]');
    if (expand && thumbStrip) {
      expand.addEventListener('click', function () {
        thumbStrip.classList.remove('is-collapsed');
        setSlide(3);
      });
    }

    var prev = wrap.querySelector('.pdp__nav--prev');
    var next = wrap.querySelector('.pdp__nav--next');
    if (prev) prev.addEventListener('click', function () { setSlide(current - 1); });
    if (next) next.addEventListener('click', function () { setSlide(current + 1); });

    var zoom = wrap.querySelector('.pdp__zoom');
    if (zoom) zoom.addEventListener('click', function () {
      var img = slides[current].querySelector('img');
      if (img) openLightbox(img.getAttribute('data-full') || img.currentSrc || img.src);
    });

    // Horizontal swipe only — vertical movement must stay a page scroll
    var x0 = null, y0 = null;
    wrap.addEventListener('touchstart', function (e) {
      x0 = e.touches[0].clientX; y0 = e.touches[0].clientY;
    }, { passive: true });
    wrap.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var dx = e.changedTouches[0].clientX - x0;
      var dy = e.changedTouches[0].clientY - y0;
      if (Math.abs(dx) > 42 && Math.abs(dx) > Math.abs(dy) * 1.4) setSlide(current + (dx < 0 ? 1 : -1));
      x0 = y0 = null;
    }, { passive: true });

    // Open on whichever slide the Liquid marked active - the chosen variant's
    // own photo - rather than always photo 1. Every other template still
    // marks the first slide, so nothing changes for them.
    var initial = 0;
    slides.forEach(function (s, idx) { if (s.classList.contains('is-active')) initial = idx; });
    setSlide(initial);
  })();

  /* ============================================================
     11. PDP VARIANTS
     ============================================================ */
  (function () {
    var form = document.querySelector('form[data-product-form]');
    var jsonEl = document.querySelector('[data-product-json]');
    if (!form || !jsonEl) return;
    // The ring template owns its own variant, price and button label through the
    // bundle tiers. Letting this block run too would overwrite both.
    if (form.querySelector('[data-engrave]')) return;
    // Same for the necklace: 7c owns the colour picker and, when present, the
    // bundle, and holds the headline price on the single-piece figure.
    if (form.querySelector('[data-nbundle], [data-nk-materials]')) return;

    var product = null;
    try { product = JSON.parse(jsonEl.textContent); } catch (e) { return; }
    if (!product || !product.variants || !product.variants.length) return;

    var idField = form.querySelector('[name="id"]');
    var priceEl = document.querySelector('[data-price]');
    var compareEl = document.querySelector('[data-compare-price]');
    var discountEl = document.querySelector('[data-discount]');
    var stickyPrice = document.querySelector('.sticky-atc__price');
    var submitBtn = form.querySelector('[type="submit"]');
    var submitSpan = submitBtn ? submitBtn.querySelector('span') : null;
    var stickyBtn = document.querySelector('[data-sticky-add]');
    var stockEl = document.querySelector('[data-stock-signal]');

    // chosen[optionIndex] = selected value
    var chosen = [];
    var groups = form.querySelectorAll('[data-option-group]');
    groups.forEach(function (g) {
      var pos = (parseInt(g.getAttribute('data-position'), 10) || 1) - 1;
      var active = g.querySelector('[data-option-value].is-active');
      chosen[pos] = active ? active.getAttribute('data-option-value') : null;
    });

    function findVariant() {
      return product.variants.find(function (v) {
        return v.options.every(function (o, i) { return chosen[i] == null || o === chosen[i]; });
      });
    }

    /* Grey out combinations that don't exist rather than letting a shopper pick a
       dead end and hit "Slutsåld" — the single biggest PDP drop-off in jewellery. */
    function refreshAvailability() {
      groups.forEach(function (g) {
        var pos = (parseInt(g.getAttribute('data-position'), 10) || 1) - 1;
        g.querySelectorAll('[data-option-value]').forEach(function (btn) {
          var val = btn.getAttribute('data-option-value');
          var possible = product.variants.some(function (v) {
            if (v.options[pos] !== val) return false;
            return v.options.every(function (o, i) {
              return i === pos || chosen[i] == null || o === chosen[i];
            }) && v.available;
          });
          btn.classList.toggle('is-oos', !possible);
        });
      });
    }

    function update() {
      var match = findVariant();
      refreshAvailability();

      if (!match) {
        if (submitSpan) submitSpan.textContent = STR.selectOptions || '';
        if (submitBtn) submitBtn.disabled = true;
        if (stickyBtn) stickyBtn.disabled = true;
        return;
      }

      if (idField) idField.value = match.id;
      if (priceEl) priceEl.textContent = money(match.price);
      if (stickyPrice) stickyPrice.textContent = money(match.price);

      var onSale = match.compare_at_price && match.compare_at_price > match.price;
      if (compareEl) {
        compareEl.hidden = !onSale;
        if (onSale) compareEl.textContent = money(match.compare_at_price);
      }
      if (discountEl) {
        discountEl.hidden = !onSale;
        if (onSale) {
          discountEl.textContent = '−' + Math.round((match.compare_at_price - match.price) / match.compare_at_price * 100) + '%';
        }
      }

      if (submitSpan) submitSpan.textContent = match.available ? (STR.addToCart || '') : (STR.soldOut || '');
      if (submitBtn) submitBtn.disabled = !match.available;

      // Keep the sticky mobile bar in step — otherwise it stays buyable after the
      // shopper switches to a sold-out colour or size.
      if (stickyBtn) {
        stickyBtn.disabled = !match.available;
        var stickySpan = stickyBtn.querySelector('span');
        if (stickySpan) stickySpan.textContent = match.available ? (STR.buy || '') : (STR.soldOutShort || '');
      }

      // Stock signal: scarcity only when it's genuinely true, never fabricated.
      // Copy comes from the locale files like everything else the script writes.
      if (stockEl) {
        var qty = match.inventory_quantity;
        var tracked = match.inventory_management === 'shopify';
        var dot = '<span class="stock-dot"></span>';
        var lowDot = '<span class="stock-dot stock-dot--low"></span>';
        if (!match.available) {
          stockEl.innerHTML = lowDot + '<span>' + esc(STR.backSoon || '') + '</span>';
          stockEl.className = 'pdp__signal pdp__signal--urgent';
        } else if (tracked && typeof qty === 'number' && qty > 0 && qty <= 5) {
          stockEl.innerHTML = lowDot + '<span>' + fill(esc(STR.lowStock || 'Endast __N__ kvar'), '__N__', '<b>' + qty + '</b>') + '</span>';
          stockEl.className = 'pdp__signal pdp__signal--urgent';
        } else {
          stockEl.innerHTML = dot + '<span>' + esc(STR.inStock || 'I lager') + '</span>';
          stockEl.className = 'pdp__signal pdp__signal--stock';
        }
      }

      // Colour choice drives the gallery so the shopper sees what they picked
      if (match.featured_image && typeof match.featured_image.position === 'number' && window.LorenzaShowSlide) {
        window.LorenzaShowSlide(match.featured_image.position - 1);
      }
    }

    form.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-option-value]');
      if (!btn) return;
      e.preventDefault();
      if (btn.classList.contains('is-oos') && btn.classList.contains('size-btn')) return;

      var group = btn.closest('[data-option-group]');
      var pos = (parseInt(group.getAttribute('data-position'), 10) || 1) - 1;
      chosen[pos] = btn.getAttribute('data-option-value');

      group.querySelectorAll('[data-option-value]').forEach(function (b) { b.classList.remove('is-active'); });
      btn.classList.add('is-active');

      var label = group.querySelector('[data-option-selected]');
      if (label) label.textContent = chosen[pos];

      update();
    });

    update();
  })();

  /* Gift-wrap toggle — purely visual state, the value is read at submit time */
  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-giftwrap] input');
    if (!input) return;
    input.closest('[data-giftwrap]').classList.toggle('is-on', input.checked);
  });

  /* ============================================================
     12. COMPLETE THE LOOK (frequently bought together)
     ============================================================ */
  (function () {
    var fbt = document.querySelector('[data-fbt]');
    if (!fbt) return;
    var totalEl = fbt.querySelector('[data-fbt-total]');
    var savingEl = fbt.querySelector('[data-fbt-saving]');
    var addBtn = fbt.querySelector('[data-fbt-add]');
    var rows = fbt.querySelectorAll('.fbt__row');

    function recalc() {
      var total = 0, compare = 0, count = 0;
      rows.forEach(function (row) {
        var input = row.querySelector('input');
        row.classList.toggle('is-on', input.checked);
        if (!input.checked) return;
        count++;
        total += parseInt(row.getAttribute('data-price'), 10) || 0;
        compare += parseInt(row.getAttribute('data-compare'), 10) || parseInt(row.getAttribute('data-price'), 10) || 0;
      });
      if (totalEl) totalEl.textContent = money(total);
      if (savingEl) {
        var save = compare - total;
        savingEl.hidden = save <= 0;
        if (save > 0) savingEl.textContent = fill(STR.youSave, '__AMT__', money(save));
      }
      if (addBtn) {
        addBtn.disabled = count === 0;
        var span = addBtn.querySelector('span');
        if (span) span.textContent = count > 1 ? fill(STR.addSelected, '__N__', count) : (STR.addToCart || '');
      }
    }

    fbt.addEventListener('change', function (e) {
      if (e.target.matches('input[type="checkbox"]')) recalc();
    });

    if (addBtn) addBtn.addEventListener('click', function () {
      var items = [];
      rows.forEach(function (row) {
        var input = row.querySelector('input');
        if (input.checked && input.value) items.push({ id: parseInt(input.value, 10), quantity: 1 });
      });
      if (items.length) addToCart(items, addBtn, { showUpsell: false, openCartAfter: true });
    });

    recalc();
  })();

  /* ============================================================
     13. STICKY MOBILE ATC
     ============================================================ */
  (function () {
    var sticky = document.querySelector('.sticky-atc');
    var anchor = document.querySelector('form[data-product-form] [type="submit"]');
    if (!sticky || !anchor) return;
    document.body.classList.add('has-sticky-atc');

    if (!('IntersectionObserver' in window)) { sticky.classList.add('is-visible'); return; }
    new IntersectionObserver(function (entries) {
      sticky.classList.toggle('is-visible', !entries[0].isIntersecting);
    }, { rootMargin: '-10px 0px 0px 0px' }).observe(anchor);

    var stickyBtn = sticky.querySelector('[data-sticky-add]');
    if (stickyBtn) stickyBtn.addEventListener('click', function () {
      // The bar shows the single-ring price, so it must never quietly add a
      // bundle at a different one. Nothing chosen yet → go to the bundle.
      if (window.LorenzaEngraveReady && !window.LorenzaEngraveReady()) {
        window.LorenzaFocusBundle();
        return;
      }
      // Reuse the real form so gift wrap and the selected variant come along
      var form = document.querySelector('form[data-product-form]');
      if (form) form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    });
  })();

  /* ============================================================
     14. SIZE GUIDE MODAL
     ============================================================ */
  var sizeGuide = document.querySelector('[data-sizeguide]');
  function closeSizeGuide() {
    if (!sizeGuide) return;
    sizeGuide.classList.remove('is-open');
    sizeGuide.setAttribute('aria-hidden', 'true');
    updateScrollLock();
  }
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-sizeguide-open]')) {
      e.preventDefault();
      if (!sizeGuide) return;
      sizeGuide.classList.add('is-open');
      sizeGuide.setAttribute('aria-hidden', 'false');
      updateScrollLock();
      return;
    }
    if (e.target.closest('[data-sizeguide-close]')) closeSizeGuide();
  });

  /* ============================================================
     15. LIGHTBOX (review photos + PDP zoom)
     ============================================================ */
  var lightbox = document.querySelector('[data-lightbox]');
  function openLightbox(src) {
    if (!lightbox || !src) return;
    var img = lightbox.querySelector('img');
    if (img) img.src = src;
    lightbox.classList.add('is-open');
    lightbox.setAttribute('aria-hidden', 'false');
    updateScrollLock();
  }
  function closeLightbox() {
    if (!lightbox) return;
    lightbox.classList.remove('is-open');
    lightbox.setAttribute('aria-hidden', 'true');
    updateScrollLock();
  }
  document.addEventListener('click', function (e) {
    var photo = e.target.closest('[data-lightbox-open]');
    if (photo) { openLightbox(photo.getAttribute('data-full') || photo.src); return; }
    if (e.target.closest('[data-lightbox-close]') || e.target === lightbox) closeLightbox();
  });

  /* ============================================================
     16. PREDICTIVE SEARCH PANEL
     ============================================================ */
  (function () {
    var panel = document.querySelector('[data-search-panel]');
    if (!panel) return;
    var input = panel.querySelector('input[type="search"]');
    var results = panel.querySelector('[data-search-results]');
    var defaults = panel.querySelector('[data-search-default]');
    var timer = null, lastQuery = '';

    function open() {
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      updateScrollLock();
      setTimeout(function () { if (input) input.focus(); }, 260);
    }
    window.__lorenzaCloseSearch = function () {
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      updateScrollLock();
    };

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-search-open]')) { e.preventDefault(); open(); return; }
      if (e.target.closest('[data-search-close]')) window.__lorenzaCloseSearch();
    });

    if (!input) return;
    input.addEventListener('input', function () {
      var q = input.value.trim();
      clearTimeout(timer);
      if (q.length < 2) {
        if (defaults) defaults.hidden = false;
        results.innerHTML = '';
        return;
      }
      // Debounced so typing on a 4G connection doesn't fire a request per keystroke
      timer = setTimeout(function () {
        if (q === lastQuery) return;
        lastQuery = q;
        var url = (ROUTES.predictive_search_url || (ROOT + 'search/suggest')) +
                  '?q=' + encodeURIComponent(q) +
                  '&resources[type]=product&resources[limit]=6&section_id=predictive-search';
        fetch(url)
          .then(function (r) { return r.text(); })
          .then(function (txt) {
            var tmp = document.createElement('div');
            tmp.innerHTML = txt;
            var list = tmp.querySelector('[data-predictive-list]');
            if (defaults) defaults.hidden = true;
            results.innerHTML = list ? list.innerHTML :
              '<p class="muted" style="padding:20px 0">' +
                esc(fill(STR.noResultsFor || '', '__Q__', q)) + '</p>';
          })
          .catch(function () {});
      }, 260);
    });
  })();
  function closeSearch() { if (window.__lorenzaCloseSearch) window.__lorenzaCloseSearch(); }

  /* ============================================================
     17. COLLECTION FILTER DRAWER
     ============================================================ */
  (function () {
    var fd = document.querySelector('.filter-drawer');
    if (!fd) return;
    var pill = document.querySelector('[data-filter-pill]');
    // The pill is fixed to the bottom, exactly where the drawer sheet slides up
    function open() {
      fd.classList.add('is-open');
      fd.setAttribute('aria-hidden', 'false');
      if (pill) pill.classList.add('is-hidden');
      updateScrollLock();
    }
    function close() {
      fd.classList.remove('is-open');
      fd.setAttribute('aria-hidden', 'true');
      if (pill) pill.classList.remove('is-hidden');
      updateScrollLock();
    }

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-filter-open]')) { e.preventDefault(); open(); return; }
      if (e.target.closest('[data-filter-close]')) { close(); return; }
      var head = e.target.closest('.filter-group__head');
      if (head) {
        var exp = head.getAttribute('aria-expanded') === 'true';
        head.setAttribute('aria-expanded', String(!exp));
        var body = head.nextElementSibling;
        if (body) body.hidden = exp;
      }
    });

    var form = fd.querySelector('[data-filter-form]');
    var countEl = fd.querySelector('[data-filter-count]');
    function updateCount() {
      if (!countEl || !form) return;
      var n = form.querySelectorAll('input[type=checkbox]:checked').length;
      countEl.textContent = n ? ' (' + n + ')' : '';
    }
    if (form) {
      form.addEventListener('change', updateCount);
      updateCount();
    }
  })();

  /* ============================================================
     18. REVIEWS — star/photo filter + pagination
     Filtering re-derives the visible set, then pagination runs over that set,
     so page counts always reflect the active filter rather than the full list.
     ============================================================ */
  (function () {
    var list = document.querySelector('[data-reviews]');
    var pager = document.querySelector('[data-review-pager]');
    if (!list) return;

    var all = Array.prototype.slice.call(list.querySelectorAll('.rv'));
    var emptyEl = list.querySelector('[data-review-empty]');
    var filters = document.querySelector('[data-review-filters]');
    var perPage = parseInt(list.getAttribute('data-per-page'), 10) || 6;

    var filtered = all.slice();
    var current = 1;
    var pageCount = 1;

    function applyFilter(mode) {
      filtered = all.filter(function (c) {
        if (mode === 'all') return true;
        if (mode === 'photo') return c.getAttribute('data-has-photo') === '1';
        return c.getAttribute('data-stars') === mode;
      });
      current = 1;
      render();
    }

    function pageNumbers(cur) {
      var keep = {};
      [1, pageCount, cur - 1, cur, cur + 1].forEach(function (n) {
        if (n >= 1 && n <= pageCount) keep[n] = 1;
      });
      var nums = Object.keys(keep).map(Number).sort(function (a, b) { return a - b; });
      var out = [], prev = 0;
      nums.forEach(function (n) { if (prev && n - prev > 1) out.push('…'); out.push(n); prev = n; });
      return out;
    }

    function renderPager() {
      if (!pager) return;
      if (pageCount < 2) { pager.innerHTML = ''; return; }
      var html = '<button type="button" data-nav="prev" aria-label="' + esc(STR.prev || '') + '"' + (current === 1 ? ' disabled' : '') + '>‹</button>';
      pageNumbers(current).forEach(function (n) {
        html += (n === '…')
          ? '<span class="rvp-dots">…</span>'
          : '<button type="button" data-page="' + n + '"' + (n === current ? ' class="is-active"' : '') + '>' + n + '</button>';
      });
      html += '<button type="button" data-nav="next" aria-label="' + esc(STR.next || '') + '"' + (current === pageCount ? ' disabled' : '') + '>›</button>';
      pager.innerHTML = html;
    }

    function render() {
      pageCount = Math.max(1, Math.ceil(filtered.length / perPage));
      current = Math.max(1, Math.min(pageCount, current));

      all.forEach(function (c) { c.style.display = 'none'; });
      filtered.forEach(function (c, i) {
        if (i >= (current - 1) * perPage && i < current * perPage) c.style.display = '';
      });
      if (emptyEl) emptyEl.hidden = filtered.length > 0;
      renderPager();
    }

    if (filters) {
      filters.addEventListener('click', function (e) {
        var chip = e.target.closest('[data-star]');
        if (!chip) return;
        filters.querySelectorAll('[data-star]').forEach(function (c) { c.classList.remove('is-active'); });
        chip.classList.add('is-active');
        applyFilter(chip.getAttribute('data-star'));
      });
    }

    if (pager) {
      pager.addEventListener('click', function (e) {
        var b = e.target.closest('button');
        if (!b || b.disabled) return;
        var nav = b.getAttribute('data-nav');
        if (nav === 'prev') current--;
        else if (nav === 'next') current++;
        else if (b.getAttribute('data-page')) current = parseInt(b.getAttribute('data-page'), 10);
        render();
        var anchor = document.getElementById('omdomen');
        if (anchor) anchor.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      });
    }

    render();
  })();

  /* ============================================================
     19. COLLECTION DESCRIPTION "READ MORE"
     ============================================================ */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-readmore]');
    if (!btn) return;
    var target = document.querySelector(btn.getAttribute('data-readmore'));
    if (!target) return;
    var clamped = target.classList.toggle('is-clamped');
    btn.textContent = clamped ? (STR.readMore || '') : (STR.readLess || '');
  });

  /* ---- Initial cart sync (populates the header bubble on every page) ---- */
  fetchCart();
})();
